import { Button, Card, CardBody, Icon } from '@repo/ui';

import type { PublicCredentialMetadata } from '../../api/clioCredentialsApi';
import { CredentialRow } from './CredentialRow';

interface CredentialListProps {
  credentials: PublicCredentialMetadata[];
  onAdd: () => void;
  onReauthorize: (c: PublicCredentialMetadata) => void;
  onEditLabel: (c: PublicCredentialMetadata) => void;
  onToggleEnabled: (c: PublicCredentialMetadata) => void;
  onRemove: (c: PublicCredentialMetadata) => void;
  busyCredentialId?: string | null;
}

export function CredentialList({
  credentials,
  onAdd,
  onReauthorize,
  onEditLabel,
  onToggleEnabled,
  onRemove,
  busyCredentialId,
}: CredentialListProps) {
  if (credentials.length === 0) {
    return (
      <Card padding="lg" variant="outlined">
        <CardBody>
          <div className="flex flex-col items-center text-center py-8 gap-4">
            <Icon name="ClioIcon" size="2xl" />
            <div>
              <h3 className="text-lg font-semibold text-xtnd-dark-800 dark:text-white">
                No Clio apps connected yet
              </h3>
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1 max-w-md">
                Connect your first Clio app to start syncing matters, bills, and trust data.
              </p>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={onAdd}
              leftIcon={<Icon name="HiPlus" className="h-4 w-4" />}
            >
              Connect your first Clio app
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  const sorted = [...credentials].sort((a, b) => {
    if (a.role === b.role) return a.label.localeCompare(b.label);
    return a.role === 'primary' ? -1 : 1;
  });

  return (
    <div className="space-y-4">
      {sorted.map((c) => (
        <CredentialRow
          key={c.credentialId}
          credential={c}
          onReauthorize={onReauthorize}
          onEditLabel={onEditLabel}
          onToggleEnabled={onToggleEnabled}
          onRemove={onRemove}
          isBusy={busyCredentialId === c.credentialId}
        />
      ))}
      <div className="pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onAdd}
          leftIcon={<Icon name="HiPlus" className="h-4 w-4" />}
        >
          Add another Clio app
        </Button>
      </div>
    </div>
  );
}
